import { CORE_STAT_DESCRIPTIONS, CORE_STAT_LABELS, type CoreStats as CoreStatsData } from '@sorrem/shared';
import classNames from 'classnames';
import { StatRow } from '../StatRow';
import { InfoIcon } from '../InfoIcon';
import styles from './CoreStats.module.scss';

/**
 * Props for rendering a character's core stats breakdown.
 */
interface CoreStatsProps {
    effectiveness: Partial<CoreStatsData>;
    /** Whether to show the explanatory note under the title. Defaults to true. */
    isNoteShown?: boolean;
    className?: string;
}

/** The core stats in the order they show. */
const CORE_STAT_ORDER: ReadonlyArray<keyof CoreStatsData> = ['melee', 'range', 'conviction', 'speed', 'dodge', 'resistance'];

/** The core stats, in the order they show, with what each describes. */
const CORE_STAT_DEFINITIONS: ReadonlyArray<{ key: keyof CoreStatsData; label: string; description: string }> = CORE_STAT_ORDER.map((key) => ({
    key,
    label: CORE_STAT_LABELS[key],
    description: CORE_STAT_DESCRIPTIONS[key],
}));

export function CoreStats({ effectiveness, isNoteShown = true, className }: CoreStatsProps): JSX.Element {

    return (
        <div className={classNames(styles.coreStats, className)}>
            <div className={styles.sectionTitle}>
                <h4>Core Stats</h4>
                <InfoIcon
                    ariaLabel="Show core stats descriptions"
                    className={styles.infoIconAnchor}
                    popoverPlacement="top"
                    popoverContent={(
                        <div className={styles.infoPopoverContent}>
                            {CORE_STAT_DEFINITIONS.map((detail) => (
                                <div key={detail.label} className={styles.infoPopoverLine}>
                                    <span className={styles.infoPopoverLabel}>{detail.label}:</span> {detail.description}
                                </div>
                            ))}
                        </div>
                    )}
                />
            </div>
            {isNoteShown && (
                <p className={styles.sectionNote}>Core stats are derived values based on your attributes, skills, and equipment. They describe your effectiveness performing certain actions.</p>
            )}
            <dl className={styles.effectivenessList}>
                {CORE_STAT_DEFINITIONS.map((definition) => (
                    <StatRow key={definition.key} label={definition.label} value={effectiveness[definition.key]} flashOnChange />
                ))}
            </dl>
        </div>
    );
}
